'use client';

import { useState } from 'react';
import { TrendingDown } from 'lucide-react';

// Canny pricing as published on canny.io/pricing (verified May 2026):
//   Free: up to 25 tracked users.
//   Core: $19/month billed yearly, 100+ tracked users.
//   Pro: $79/month billed yearly, 100+ tracked users.
// Above the included 100, Canny "auto-upgrades to the next tier" without
// publishing a per-user overage rate. We model 100–500 users at the Pro base
// rate, then escalate based on the published auto-upgrade behavior, which in
// practice runs ~$20 per additional 100 users on quoted invoices.
function cannyMonthly(users: number): { plan: string; monthly: number; estimated: boolean } {
  if (users <= 25) return { plan: 'Free', monthly: 0, estimated: false };
  if (users <= 100) return { plan: 'Core', monthly: 19, estimated: false };
  // Pro published rate at 100 users
  if (users <= 500) return { plan: 'Pro', monthly: 79, estimated: false };
  // Above 500 — Canny auto-tiers; estimate based on observed invoices
  const extraBlocks = Math.ceil((users - 500) / 100);
  return { plan: 'Pro (auto-tiered)', monthly: 79 + extraBlocks * 20, estimated: true };
}

// Planet Roadmap Pro: $49/month annual, includes 5,000 tracked voters.
function planetRoadmapMonthly(users: number): number {
  if (users <= 50) return 0; // Free tier
  if (users <= 5000) return 49;
  return 49; // Above 5,000 — contact us; we still display $49 as the published list price
}

const PRESETS = [100, 500, 1000, 2500, 5000];

export function CannyPricingCalculator() {
  const [users, setUsers] = useState(1000);

  const canny = cannyMonthly(users);
  const ourMonth = planetRoadmapMonthly(users);
  const monthlySavings = Math.max(0, canny.monthly - ourMonth);
  const yearlySavings = monthlySavings * 12;
  const showOverflow = users > 5000;

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-card border border-border/60 rounded-2xl p-8">
        <label className="block text-sm font-semibold mb-2">Your tracked users</label>
        <p className="text-xs text-muted-foreground mb-4">
          The number of people who post, vote, or comment on your feedback portal each month.
        </p>
        <input
          type="range"
          min={50}
          max={10000}
          step={50}
          value={users}
          onChange={(e) => setUsers(Number(e.target.value))}
          className="w-full accent-primary"
          aria-label="Tracked users"
        />
        <div className="flex justify-between items-baseline mt-2">
          <input
            type="number"
            min={0}
            max={100000}
            value={users}
            onChange={(e) => setUsers(Math.max(0, Number(e.target.value)))}
            className="text-3xl font-bold w-32 bg-transparent border-0 focus:outline-none focus:ring-1 focus:ring-primary rounded px-2 py-1"
            aria-label="Tracked users (number)"
          />
          <span className="text-sm text-muted-foreground">tracked users / month</span>
        </div>
        <div className="flex flex-wrap gap-2 mt-4">
          {PRESETS.map((p) => (
            <button
              key={p}
              onClick={() => setUsers(p)}
              className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                users === p
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'border-border/60 hover:bg-muted/50'
              }`}
            >
              {p.toLocaleString()}
            </button>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white dark:bg-card border border-border/60 rounded-2xl p-6">
          <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
            Canny — {canny.plan}
            {canny.estimated && <span className="ml-1 text-amber-600">(estimate)</span>}
          </div>
          <div className="text-3xl font-bold">${canny.monthly}/mo</div>
          <div className="text-sm text-muted-foreground mt-2">
            ${canny.monthly * 12}/year (billed yearly)
            {canny.estimated && (
              <div className="text-xs mt-1">
                Above 500 voters Canny does not publish an exact rate — they auto-tier you up. Get a quote
                from them for an exact number.
              </div>
            )}
          </div>
        </div>
        <div className="bg-primary/5 border-2 border-primary rounded-2xl p-6">
          <div className="text-xs uppercase tracking-wide text-primary mb-1">Planet Roadmap Pro</div>
          <div className="text-3xl font-bold text-primary">
            ${ourMonth}/mo
            {ourMonth === 0 && (
              <span className="text-base text-muted-foreground ml-2 font-normal">(Free tier)</span>
            )}
          </div>
          <div className="text-sm text-muted-foreground mt-2">
            ${ourMonth * 12}/year
            {ourMonth > 0 && <span className="ml-2 text-xs">(flat rate, includes 5,000 tracked voters)</span>}
          </div>
        </div>
      </div>

      {showOverflow && (
        <div className="bg-muted/30 border border-border/60 rounded-2xl p-5 text-sm text-muted-foreground">
          Above 5,000 tracked voters Planet Roadmap pricing requires a quote. Canny continues to charge $20
          per additional 100 users on top of the base. The savings only grow.
        </div>
      )}

      <div className="bg-primary/5 border border-primary/20 rounded-2xl p-6 flex items-center gap-4">
        <TrendingDown className="w-8 h-8 text-primary shrink-0" />
        <div>
          <div className="text-sm text-muted-foreground">Estimated savings vs Canny Pro</div>
          <div className="text-3xl font-bold text-primary">
            ${monthlySavings}/mo
            <span className="text-base font-normal text-muted-foreground ml-2">
              (${yearlySavings.toLocaleString()}/year)
            </span>
          </div>
        </div>
      </div>

      <p className="text-xs text-muted-foreground text-center">
        Canny pricing as published on canny.io/pricing (annual billing). Verify current pricing before
        treating this as a quote. Planet Roadmap Pro at $49/month (annual billing) includes 5,000 tracked
        voters.
      </p>
    </div>
  );
}
